import { useMemo, useState, type ReactNode } from "react";
import { ArrowDown, ArrowUp, ArrowUpDown, Search, X } from "lucide-react";
import { cn } from "@clevis/shared-ui";

// Long lists, done once: filter as you type, sort by a column, show 25 at a
// time with "show more". Used by Team (people, invites) and any list that can
// grow past a screenful.

export type SortDir = "asc" | "desc";

export function useListView<T>(
  rows: T[],
  opts: {
    /** The text a row is searched by (all terms must match). */
    text: (row: T) => Array<string | null | undefined>;
    sorts: Record<string, (a: T, b: T) => number>;
    defaultSort: string;
    defaultDir?: SortDir;
    pageSize?: number;
    /** Extra filter (role, department…) applied before search. */
    filter?: (row: T) => boolean;
  },
) {
  const pageSize = opts.pageSize ?? 25;
  const [query, setQuery] = useState("");
  const [sortKey, setSortKey] = useState(opts.defaultSort);
  const [sortDir, setSortDir] = useState<SortDir>(opts.defaultDir ?? "asc");
  const [limit, setLimit] = useState(pageSize);
  const { text, sorts, filter } = opts;

  const filtered = useMemo(() => {
    const terms = query.toLowerCase().split(/\s+/).filter(Boolean);
    const base = filter ? rows.filter(filter) : rows;
    const hits = terms.length
      ? base.filter((r) => {
          const hay = text(r).filter(Boolean).join(" ").toLowerCase();
          return terms.every((t) => hay.includes(t));
        })
      : base;
    const cmp = sorts[sortKey] ?? (() => 0);
    return [...hits].sort((a, b) => (sortDir === "asc" ? cmp(a, b) : -cmp(a, b)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, query, sortKey, sortDir, filter]);

  return {
    query,
    setQuery: (q: string) => {
      setQuery(q);
      setLimit(pageSize);
    },
    sortKey,
    sortDir,
    toggleSort: (key: string) => {
      if (key === sortKey) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
      else {
        setSortKey(key);
        setSortDir("asc");
      }
    },
    /** Every row that matches (for "select all"). */
    filtered,
    page: filtered.slice(0, limit),
    total: rows.length,
    more: () => setLimit((n) => n + pageSize),
    all: () => setLimit(Number.MAX_SAFE_INTEGER),
    pageSize,
  };
}

export function SearchBox({ value, onChange, placeholder, className }: { value: string; onChange: (v: string) => void; placeholder: string; className?: string }) {
  return (
    <label className={cn("relative flex min-w-0 items-center", className)}>
      <Search size={14} className="pointer-events-none absolute left-2.5 text-steel" />
      <input
        className="w-full rounded-lg border border-steel/40 bg-white py-1.5 pl-8 pr-7 text-base text-ink placeholder:text-steel focus:border-brandorange focus:outline-none focus:ring-1 focus:ring-brandorange sm:text-sm"
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Escape") onChange("");
        }}
      />
      {value ? (
        <button type="button" aria-label="Clear search" className="absolute right-2 text-steel hover:text-ink" onClick={() => onChange("")}>
          <X size={14} />
        </button>
      ) : null}
    </label>
  );
}

export function SortHeader({
  label,
  k,
  view,
  className,
}: {
  label: string;
  k: string;
  view: { sortKey: string; sortDir: SortDir; toggleSort: (k: string) => void };
  className?: string;
}) {
  const on = view.sortKey === k;
  const Icon = !on ? ArrowUpDown : view.sortDir === "asc" ? ArrowUp : ArrowDown;
  return (
    <button
      type="button"
      className={cn("group flex items-center gap-1 whitespace-nowrap text-left text-[11px] font-semibold uppercase tracking-wide", on ? "text-ink" : "text-steel hover:text-ink", className)}
      onClick={() => view.toggleSort(k)}
      aria-label={`Sort by ${label}`}
    >
      {label}
      <Icon size={11} className={cn(on ? "opacity-100" : "opacity-0 group-hover:opacity-60")} />
    </button>
  );
}

export function ListFooter({ shown, matching, total, onMore, onAll, noun }: { shown: number; matching: number; total: number; onMore: () => void; onAll: () => void; noun: string }) {
  if (matching === 0) return null;
  return (
    <div className="flex flex-wrap items-center justify-between gap-2 border-t border-steel/10 px-4 py-2 text-xs text-steel">
      <span>
        Showing {Math.min(shown, matching)} of {matching}
        {matching !== total ? ` matching (${total} ${noun})` : ` ${noun}`}
      </span>
      {shown < matching ? (
        <span className="flex items-center gap-3">
          <button className="font-semibold text-brandorange-dark hover:text-brandorange" onClick={onMore}>
            Show {Math.min(25, matching - shown)} more
          </button>
          <button className="hover:text-ink" onClick={onAll}>
            Show all
          </button>
        </span>
      ) : null}
    </div>
  );
}

/** Round initials badge (or the person's photo). */
export function Avatar({ name, photoUrl, size = 28 }: { name: string; photoUrl?: string | null; size?: number }) {
  const initials = name
    .replace(/@.*/, "")
    .split(/[\s._-]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]!.toUpperCase())
    .join("");
  if (photoUrl) return <img src={photoUrl} alt="" className="shrink-0 rounded-full object-cover" style={{ width: size, height: size }} />;
  return (
    <span
      className="flex shrink-0 items-center justify-center rounded-full bg-ink/10 font-semibold text-ink"
      style={{ width: size, height: size, fontSize: Math.round(size * 0.4) }}
      aria-hidden="true"
    >
      {initials || "?"}
    </span>
  );
}

/**
 * The bar that appears when rows are ticked: "3 selected · [actions] · Clear".
 * It floats at the bottom of the screen so ticking never moves the rows under
 * the pointer (no transform: dropdowns inside it position against the window).
 */
export function BulkBar({ count, onClear, children }: { count: number; onClear: () => void; children: ReactNode }) {
  if (count === 0) return null;
  return (
    <div className="pointer-events-none fixed inset-x-0 bottom-4 z-40 flex justify-center px-4">
      <div className="pointer-events-auto flex max-w-full flex-wrap items-center gap-2 rounded-xl border border-ink/15 bg-ink px-4 py-2 text-sm text-white shadow-2xl shadow-ink/30">
        <span className="font-semibold">{count} selected</span>
        <span className="h-4 w-px bg-white/25" />
        {children}
        <button className="ml-1 text-white/70 hover:text-white" onClick={onClear}>
          Clear
        </button>
      </div>
    </div>
  );
}
